import { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
// pages
import { useDispatch, useSelector } from 'react-redux';
import {
  currentWorkspaceSelectors,
  userSelectors,
  uiSelectors,
  workspaceSelectors,
} from '../../selectors';
import { PATH_AUTH, PATH_PAGE } from '../../routes/paths';
import LoadingScreen from 'src/minimals/LoadingScreen';
import { setCurrentWorkspace } from '../../redux/slices/currentWorkspace';
import _ from 'lodash';
import { loadDefaultData } from 'src/redux/thunks/currentWorkspace';
import { getWorkspaceByIdAsSuperAdmin } from 'src/redux/thunks/workspaces';

// ----------------------------------------------------------------------

export default function AuthGuard({ children, to }) {
  const { workspaceId } = useParams();
  const isAuthenticated = useSelector(userSelectors.isAuth());

  const userWorkspace = useSelector(
    workspaceSelectors.getWorkspaceById(workspaceId)
  );

  const hasWorkspaces = useSelector(workspaceSelectors.hasWorkspaces);

  const hasWorkspacesInvitations = useSelector(
    workspaceSelectors.hasWorkspacesInvitations
  );

  const isWorkspaceLoading = useSelector(
    workspaceSelectors.isWorkspaceLoading()
  );

  const currentWorkspace = useSelector(
    currentWorkspaceSelectors.getCurrentWorkspace
  );

  const isLoggedAsCustomer = useSelector(uiSelectors.isLoggedAsCustomer);
  const isSuperAdmin = useSelector(userSelectors.isSuperAdmin());
  const superAdminLookup = useSelector(workspaceSelectors.getSuperAdminLookup);

  // A superadmin landed on /workspace/<id> for a workspace they hold no
  // user_role in, so it is absent from the entity store.
  const needsSuperAdminLookup = Boolean(
    isSuperAdmin && workspaceId && !userWorkspace
  );
  const superAdminLookupFailed =
    superAdminLookup.status === 'failed' &&
    superAdminLookup.id === workspaceId;

  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [requestedLocation, setRequestedLocation] = useState(null);

  useEffect(() => {
    if (isAuthenticated && needsSuperAdminLookup && !superAdminLookupFailed) {
      // Re-entrancy is handled by the thunk's `condition`, not here.
      dispatch(getWorkspaceByIdAsSuperAdmin(workspaceId));
    }
  }, [
    isAuthenticated,
    needsSuperAdminLookup,
    superAdminLookupFailed,
    workspaceId,
    dispatch,
  ]);
  useEffect(() => {
    if (
      workspaceId &&
      isAuthenticated &&
      userWorkspace &&
      currentWorkspace?.id !== workspaceId
    ) {
      dispatch(setCurrentWorkspace(userWorkspace.id));
      // TODO: esto se llama aca porque se necesita cargar también la data
      // del workspace si no es el mismo que se consulta por url
      // NOTA: evaluar si persistir la sesion en backend con currentWorkspace
      // en user es la forma correcta de hacerlo (podria ser localStorage)
      dispatch(loadDefaultData(userWorkspace.id));
    }
  }, [
    workspaceId,
    isAuthenticated,
    userWorkspace,
    dispatch,
    currentWorkspace?.id,
  ]);

  if (!isAuthenticated) {
    if (pathname !== requestedLocation) {
      setRequestedLocation(pathname);
    }
    return <Navigate to={PATH_AUTH.root} />;
  }

  if (isWorkspaceLoading) {
    return <LoadingScreen />;
  }

  // Placement matters. Sitting above the !hasWorkspaces and !userWorkspace
  // redirects below is what lets a superadmin — including one with zero
  // memberships — reach a foreign workspace without being bounced to
  // /workspaces or /create-workspace mid-fetch. Once the lookup fails, the gate
  // opens and those same redirects handle a bad UUID normally.
  if (needsSuperAdminLookup && !superAdminLookupFailed) {
    return <LoadingScreen />;
  }

  if (!hasWorkspaces && hasWorkspacesInvitations) {
    return <Navigate to={PATH_PAGE.workspaces} />;
  }

  if (!hasWorkspaces && isLoggedAsCustomer) {
    return <>{children}</>;
  }

  if (!hasWorkspaces) {
    return <Navigate to={PATH_PAGE.createWorkspace} />;
  }

  if (
    (_.isEmpty(currentWorkspace) || (workspaceId && !userWorkspace)) &&
    to === 'dashboard'
  ) {
    return <Navigate to={PATH_PAGE.workspaces} />;
  }

  if (requestedLocation && pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
}
